import { useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import { Badge, Button, Card, Col, Dropdown, Form, Modal, Row, Spinner, Table } from "react-bootstrap";
import { Link } from "react-router-dom";
import swal from "sweetalert";
import api from "../../../api";
import PortalMenu from "../Common/PortalMenu";
import usePermission from "../../../hooks/usePermission";
import PageTitle from "../../layouts/PageTitle";
import AppPagination from "../Common/AppPagination";
import SignatureModal from "../Common/SignatureModal";

const formatDate = (value) => {
 if (!value) return "-";
 const date = new Date(value);
 if (Number.isNaN(date.getTime())) return "-";
 return date.toLocaleDateString("en-IN", {
 day: "2-digit",
 month: "short",
 year: "numeric",
 });
};

const getWorkOrderLabel = (workOrder) => {
 if (!workOrder) return "-";
 if (typeof workOrder === "string") return workOrder;
 return workOrder.woNo || workOrder.woNumber || workOrder.number || "-";
};

const getProjectLabel = (project) => {
 if (!project) return "-";
 if (typeof project === "string") return project;
 return project.projectName || project.name || "-";
};

const buildFilteredListRoute = (baseRoute, record = {}) => {
 const [path, queryString = ""] = String(baseRoute || "").split("?");
 const params = new URLSearchParams(queryString);

 const workOrderId =
 typeof record.workOrder === "object"
 ? record.workOrder?._id
 : record.raw?.workOrder?._id || record.raw?.workOrder || record.workOrder;

 const projectId =
 typeof record.project === "object"
 ? record.project?._id
 : record.raw?.project?._id || record.raw?.project || record.project;

 if (workOrderId) params.set("workOrder", workOrderId);
 if (projectId) params.set("project", projectId);

 const finalQuery = params.toString();
 return finalQuery ? `${path}?${finalQuery}` : path;
};

const getStatusVariant = (status) => {
 const value = String(status || "").toLowerCase();
 if (value.includes("approved")) return "success";
 if (value.includes("reject")) return "danger";
 if (value.includes("pending") || value.includes("submitted")) return "warning";
 if (value.includes("draft")) return "secondary";
 return "info";
};

const getApprovalEndpoint = (record, action) => {
 switch (record.module) {
 case "Documents":
 return `/documents/${record.id}`;
 case "DPR":
 return action === "approve" ? `/dpr/${record.id}/approve` : `/dpr/${record.id}/reject`;
 case "Billing":
 return action === "approve" ? `/billing/${record.id}/approve` : `/billing/${record.id}/reject`;
 case "MIR":
 return action === "approve" ? `/mir/${record.id}/approve` : `/mir/${record.id}/reject`;
 case "Measurement Sheets":
 return action === "approve" ? `/measurement-sheets/${record.id}/approve` : `/measurement-sheets/${record.id}/reject`;
 case "MOM":
 return action === "approve" ? `/minutesofmeetings/${record.id}/approve` : `/minutesofmeetings/${record.id}/reject`;
 case "TDS/Manual":
 return `/document-control/${record.id}`;
 case "Statutory Approvals":
 return `/statutory-approvals/${record.id}`;
 case "Indent":
 return action === "approve" ? `/indents/${record.id}/approve` : `/indents/${record.id}/reject`;
 case "DC":
 return action === "approve" ? `/deliveries/${record.id}/approve` : `/deliveries/${record.id}/reject`;
 case "Contractor DC":
 return action === "approve" ? `/contractor-delivery/${record.id}/approve` : `/contractor-delivery/${record.id}/reject`;
 default:
 return record.route;
 }
};

const needsSignatureForApproval = (record) =>
 ["Documents", "DPR", "Billing", "MIR", "Measurement Sheets", "MOM", "TDS/Manual", "Indent", "DC", "Contractor DC"].includes(record.module);

const showStampUploadForApproval = (record) => record.module === "TDS/Manual";

const resolveAttachmentUrl = (fileUrl) => {
 const value = String(fileUrl || "").trim();
 if (!value) return "";
 if (/^data:/i.test(value)) return value;

 const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
 const toBackendUploadsUrl = (pathname) => {
 const cleanPath = String(pathname || "").replace(/\\/g, "/");
 if (!cleanPath) return "";
 if (/^https?:\/\//i.test(cleanPath)) return cleanPath;
 if (apiBase) return `${apiBase}${cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`}`;
 return cleanPath.startsWith("/") ? cleanPath : `/${cleanPath}`;
 };

 if (/^https?:\/\//i.test(value)) {
 try {
 const parsed = new URL(value);
 if (/amazonaws\.com|localhost|127\.0\.0\.1/i.test(parsed.hostname)) {
 return toBackendUploadsUrl(parsed.pathname);
 }
 return value;
 } catch (_) {
 return value;
 }
 }

 const normalized = value.replace(/\\/g, "/").replace(/^\/+/, "");
 return toBackendUploadsUrl(`/${normalized}`);
};

const SOURCES = [
 {
 key: "documents",
 label: "Documents",
 permission: "approve:documents",
 endpoint: "/documents",
 params: { status: "Pending Approval", page: 1, limit: 200 },
 route: "/documents?status=Pending%20Approval",
 normalize: (items = []) =>
 items.map((item) => ({
 id: item._id,
 module: "Documents",
 reference: item.fileName || item.documentType?.name || "Document",
 status: item.status || "Pending Approval",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.submittedBy?.name || "Unknown",
 date: item.submissionDate || item.createdAt,
 route: "/documents?status=Pending%20Approval",
 actionRoute: buildFilteredListRoute("/documents?status=Pending%20Approval", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "dprs",
 label: "DPR",
 permission: "approve:dprs",
 endpoint: "/dpr",
 params: { page: 1, limit: 200 },
 route: "/dpr",
 normalize: (items = []) =>
 items
 .filter((item) => ["draft", "submitted"].includes(String(item.status || "").toLowerCase()))
 .map((item) => ({
 id: item._id,
 module: "DPR",
 reference: item.dprNumber || "DPR",
 status: item.status || "Draft",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.employeeUser?.name || item.submittedBy?.name || "Unknown",
 date: item.date || item.createdAt,
 route: "/dpr",
 actionRoute: buildFilteredListRoute("/dpr", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "mirs",
 label: "MIR",
 permission: "approve:mirs",
 endpoint: "/mir",
 params: { status: "Pending", page: 1, limit: 200 },
 route: "/mir?status=Pending",
 normalize: (items = []) =>
 items.map((item) => ({
 id: item._id,
 module: "MIR",
 reference: item.mirNumber || "MIR",
 status: item.approvalStatus || item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.submittedBy?.name || item.createdBy?.name || "Unknown",
 date: item.createdAt || item.inspectionDate,
 route: "/mir?status=Pending",
 actionRoute: buildFilteredListRoute("/mir?status=Pending", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "measurementSheets",
 label: "Measurement Sheets",
 displayLabel: "JMR",
 permission: "approve:measurement-sheets",
 endpoint: "/measurement-sheets",
 params: { status: "Submitted", page: 1, limit: 200 },
 route: "/measurement-sheets?status=Submitted",
 normalize: (items = []) =>
 items.map((item) => ({
 id: item._id,
 module: "Measurement Sheets",
 reference: item.sheetNumber || "Measurement Sheet",
 status: item.status || "Submitted",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.submittedBy?.name || item.createdBy?.name || "Unknown",
 date: item.preparedDate || item.createdAt,
 route: "/measurement-sheets?status=Submitted",
 actionRoute: buildFilteredListRoute("/measurement-sheets?status=Submitted", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "moms",
 label: "MOM",
 permission: "approve:moms",
 endpoint: "/mom",
 params: { status: "Pending", page: 1, limit: 200 },
 route: "/mom/list?status=Pending",
 normalize: (items = []) =>
 items.map((item) => ({
 id: item._id,
 module: "MOM",
 reference: item.momNumber || "MOM",
 status: item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.createdBy?.name || item.submittedBy?.name || "Unknown",
 date: item.meetingDate || item.createdAt,
 route: "/mom/list?status=Pending",
 actionRoute: buildFilteredListRoute("/mom/list?status=Pending", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "tds",
 label: "TDS/Manual",
 permission: "approve:documents",
 endpoint: "/document-control",
 params: { type: "TDS", status: "Pending Approval", page: 1, limit: 200 },
 route: "/tds",
 normalize: (items = []) =>
 items
 .filter((item) => String(item.status || "").toLowerCase() !== "approved")
 .map((item) => ({
 id: item._id,
 module: "TDS/Manual",
 reference: item.productName || item.fileName || item.documentType?.name || "TDS",
 status: item.status || "Pending Approval",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.submittedBy?.name || item.createdBy?.name || "Unknown",
 date: item.submissionDate || item.createdAt,
 route: "/tds",
 actionRoute: buildFilteredListRoute("/tds", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "statutoryApprovals",
 label: "Statutory Approvals",
 permission: "approve:statutory-approvals",
 endpoint: "/statutory-approvals",
 params: { page: 1, limit: 200 },
 route: "/statutory-approvals?status=Pending",
 normalize: (items = []) =>
 items
 .filter((item) =>
 ["pending", "submitted"].includes(String(item.status || "").toLowerCase())
 )
 .map((item) => ({
 id: item._id,
 module: "Statutory Approvals",
 reference: item.approvalNo || item.certificateNo || item.approvalType || "Approval",
 status: item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.createdBy?.name || "Unknown",
 date: item.issueDate || item.createdAt,
 route: "/statutory-approvals?status=Pending",
 actionRoute: buildFilteredListRoute("/statutory-approvals?status=Pending", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "billings",
 label: "Billings",
 permission: "approve:billings",
 endpoint: "/billing",
 params: { page: 1, limit: 200 },
 route: "/billings",
 normalize: (items = []) =>
 items
 .filter((item) =>
 ["draft", "submitted", "pending"].includes(String(item.status || "").toLowerCase())
 )
 .map((item) => ({
 id: item._id,
 module: "Billing",
 reference: item.invoiceNumber || "Billing",
 status: item.status || "Draft",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.createdBy?.name || item.submittedBy?.name || "Unknown",
 date: item.createdAt || item.submittedDate,
 route: "/billings",
 actionRoute: buildFilteredListRoute("/billings", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "indents",
 label: "Indent",
 permission: "read:indents",
 approvePermission: "approve:indents",
 endpoint: "/indents",
 params: { status: "Pending", page: 1, limit: 200 },
 route: "/indent",
 normalize: (items = []) =>
 items
 .filter((item) =>
 String(item.status || "").toLowerCase() === "pending"
 )
 .map((item) => ({
 id: item._id,
 module: "Indent",
 reference: item.indentNumber || "Indent",
 status: item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.requestedBy?.name || item.createdBy?.name || "Unknown",
 date: item.createdAt,
 route: "/indent",
 actionRoute: buildFilteredListRoute("/indent", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "deliveries",
 label: "DC",
 permission: "read:deliveries",
 approvePermission: "approve:deliveries",
 endpoint: "/deliveries",
 params: { status: "Pending", page: 1, limit: 200 },
 route: "/deliveries",
 normalize: (items = []) =>
 items
 .filter((item) =>
 String(item.status || "").toLowerCase() === "pending"
 )
 .map((item) => ({
 id: item._id,
 module: "DC",
 reference: item.deliveryNumber || item.challanNumber || "Delivery",
 status: item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.receivedBy?.name || item.createdBy?.name || "Unknown",
 date: item.deliveryDate || item.createdAt,
 route: "/deliveries",
 actionRoute: buildFilteredListRoute("/deliveries", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
 {
 key: "contractorDeliveries",
 label: "Contractor DC",
 permission: "read:contractor-deliveries",
 approvePermission: "approve:contractor-deliveries",
 endpoint: "/contractor-delivery",
 params: { status: "Pending", page: 1, limit: 200 },
 route: "/contractor-delivery",
 normalize: (items = []) =>
 items
 .filter((item) =>
 String(item.status || "").toLowerCase() === "pending"
 )
 .map((item) => ({
 id: item._id,
 module: "Contractor DC",
 reference: item.deliveryNumber || item.challanNumber || "Contractor DC",
 status: item.status || "Pending",
 project: getProjectLabel(item.project),
 workOrder: getWorkOrderLabel(item.workOrder),
 remarks: item.remarks || "",
 owner: item.createdBy?.name || item.contractor?.name || "Unknown",
 date: item.deliveryDate || item.createdAt,
 route: "/contractor-delivery",
 actionRoute: buildFilteredListRoute("/contractor-delivery", {
 workOrder: item.workOrder,
 project: item.project,
 raw: item,
 }),
 raw: item,
 })),
 },
];

const PendingApprovals = () => {
 const { hasPermission, isAdmin, role } = usePermission();
 const [records, setRecords] = useState([]);
 const [selectedRecord, setSelectedRecord] = useState(null);
 const [showViewModal, setShowViewModal] = useState(false);
 const [showSignModal, setShowSignModal] = useState(false);
 const [pendingAction, setPendingAction] = useState(null);
 const [loading, setLoading] = useState(true);
 const [refreshing, setRefreshing] = useState(false);
 const [search, setSearch] = usePersistentState("pending_approvals_search", "");
 const [moduleFilter, setModuleFilter] = usePersistentState("pending_approvals_module", "all");
 const [currentPage, setCurrentPage] = usePersistentState("pending_approvals_page", 1);
 const pageSize = 10;

 const visibleSources = useMemo(
 () =>
 SOURCES.filter((source) =>
 isAdmin || hasPermission(source.permission)
 ),
 [hasPermission, isAdmin]
 );

 const loadApprovals = async () => {
 try {
 setLoading(true);

 const results = await Promise.allSettled(
 visibleSources.map(async (source) => {
 const res = await api.get(source.endpoint, { params: source.params });
 if (!res.data?.success) {
 return { source, items: [] };
 }
 return { source, items: source.normalize(res.data.data || []) };
 })
 );

 const flattened = results.flatMap((result) => {
 if (result.status !== "fulfilled") return [];
 return result.value.items
  .filter((item) => item.project && item.project !== "-")
  .map((item) => ({
  ...item,
  sourceLabel: result.value.source.label,
 }));
 });

 flattened.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
 setRecords(flattened);
 } catch (error) {
 console.error("Failed to load pending approvals", error);
 swal("Error", "Failed to load pending approvals", "error");
 setRecords([]);
 } finally {
 setLoading(false);
 setRefreshing(false);
 }
 };

 useEffect(() => {
 loadApprovals();
 // eslint-disable-next-line react-hooks/exhaustive-deps
 }, [visibleSources.length]);

 useEffect(() => {
 setCurrentPage(1);
 }, [search, moduleFilter]);

 const filteredRecords = records.filter((record) => {
 const query = search.trim().toLowerCase();
 const moduleMatch = moduleFilter === "all" || record.module === moduleFilter;
 const searchMatch =
 !query ||
 [record.module, record.reference, record.project, record.workOrder, record.status, record.owner, record.remarks]
 .filter(Boolean)
 .some((value) => String(value).toLowerCase().includes(query));
 return moduleMatch && searchMatch;
 });

 const totalFiltered = filteredRecords.length;
 const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
 const safePage = Math.min(currentPage, totalPages);
 const paginatedRecords = filteredRecords.slice(
 (safePage - 1) * pageSize,
 safePage * pageSize
 );

 const summary = visibleSources.map((source) => {
 const count = records.filter((item) => item.sourceLabel === source.label).length;
 return { ...source, count };
 });

 const totalPending = records.length;
 const selectedRecordAttachments = useMemo(() => {
 if (!selectedRecord?.raw) return [];

 const raw = selectedRecord.raw;
 const attachments = [];

 if (Array.isArray(raw.documents)) attachments.push(...raw.documents);
 if (Array.isArray(raw.files)) attachments.push(...raw.files);
 if (raw.document?.fileUrl) attachments.push(raw.document);
 if (raw.fileUrl) {
 attachments.push({
 fileUrl: raw.fileUrl,
 fileName: raw.fileName || selectedRecord.reference,
 });
 }

 return attachments.filter((file) => file?.fileUrl);
 }, [selectedRecord]);

 const openViewModal = (record) => {
 setSelectedRecord(record);
 setShowViewModal(true);
 };

 const refreshApprovals = async () => {
 setRefreshing(true);
 try {
 await loadApprovals();
 } finally {
 setRefreshing(false);
 }
 };

 const executeApprovalAction = async ({ record, action, remarks = "", signatureFile = null, stampFile = null }) => {
 const endpoint = getApprovalEndpoint(record, action);
 const status = action === "approve" ? "Approved" : "Rejected";

 try {
 let response;

 if (record.module === "Documents") {
 const formData = new FormData();
 formData.append("status", status);
 formData.append("remarks", remarks);
 if (action === "approve" && signatureFile) {
 formData.append("signature", signatureFile);
 }
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else if (record.module === "DPR") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("signature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "Billing") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("clientSignature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "MIR") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("signature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "Measurement Sheets") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("signature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "MOM") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("clientSignature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "TDS/Manual") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("status", "Approved");
 if (remarks) formData.append("remarks", remarks);
 formData.append("signature", signatureFile);
 if (stampFile) formData.append("stamp", stampFile);
 response = await api.put(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.put(endpoint, { status: "Rejected", remarks });
 }
 } else if (record.module === "Statutory Approvals") {
 response = await api.patch(endpoint, {
 status,
 remarks,
 });
 } else if (record.module === "Indent") {
 if (action === "approve") {
 const formData = new FormData();
 formData.append("signature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.patch(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.patch(endpoint, { remarks });
 }
 } else if (record.module === "DC") {
 // Delivery — PUT with signature for approve, PUT with remarks for reject
 if (action === "approve") {
 const formData = new FormData();
 formData.append("signature", signatureFile);
 if (remarks) formData.append("remarks", remarks);
 response = await api.put(endpoint, formData, {
 headers: { "Content-Type": "multipart/form-data" },
 });
 } else {
 response = await api.put(endpoint, { remarks });
 }
 }

 if (response?.data?.success) {
 swal("Success", `${record.module} ${status.toLowerCase()} successfully!`, "success");
 setShowSignModal(false);
 setPendingAction(null);
 await loadApprovals();
 } else {
 swal("Error", `Failed to ${action} record`, "error");
 }
 } catch (error) {
 swal("Error", error.response?.data?.message || `Failed to ${action} record`, "error");
 }
 };

 const handleApprove = (record) => {
 if (needsSignatureForApproval(record)) {
 setPendingAction({ record, action: "approve" });
 setShowSignModal(true);
 return;
 }

 executeApprovalAction({ record, action: "approve" });
 };

 const handleReject = async (record) => {
 const remarks = window.prompt(`Enter rejection reason for ${record.module}:`);
 if (!remarks?.trim()) return;

 await executeApprovalAction({ record, action: "reject", remarks: remarks.trim() });
 };

 const handleSignatureSave = async (file, stampFile, remarks = "") => {
 if (!pendingAction?.record) return;
 await executeApprovalAction({
 record: pendingAction.record,
 action: pendingAction.action,
 remarks: remarks || pendingAction.remarks || "",
 signatureFile: file,
 stampFile,
 });
 };

 const canUseApprovalAction = (record, action) => {
 const isClient = ["client", "po"].includes(String(role || "").toLowerCase());
 return isAdmin || isClient;
 };

 return (
 <>
 <PageTitle activeMenu="Pending Approvals" motherMenu="Approvals" />
 <Card className="shadow-sm border-0 overflow-hidden">
 <Card.Header className="bg-white border-0 p-4">
 <Row className="align-items-center mb-3 g-2">
 <Col>
 <div className="d-flex align-items-center gap-2 flex-wrap">
 <h4 className="mb-0">Pending Approvals</h4>
 <Badge bg="light" text="dark" className="rounded-pill px-3 py-2">
 {totalFiltered} showing
 </Badge>
 <Badge bg="secondary" className="rounded-pill px-3 py-2">
 {totalPending} total
 </Badge>
 </div>
 </Col>
 <Col xs="auto">
 <Button
 variant="outline-primary"
 className="rounded-pill px-4"
 onClick={refreshApprovals}
 disabled={loading || refreshing}
 >
 {loading || refreshing ? "Refreshing..." : "Refresh"}
 </Button>
 </Col>
 </Row>
 <Row className="g-3 align-items-center">
 <Col lg={6}>
 <Form.Control
 type="text"
 className="rounded-pill"
 placeholder="Search reference, project, work order, owner, remarks..."
 value={search}
 onChange={(e) => setSearch(e.target.value)}
 />
 </Col>
 <Col lg={4}>
 <Form.Select
 className="rounded-pill"
 value={moduleFilter}
 onChange={(e) => setModuleFilter(e.target.value)}
 >
 <option value="all">All modules</option>
 {summary.map((item) => (
 <option key={item.key} value={item.label}>
 {item.displayLabel || item.label}
 </option>
 ))}
 </Form.Select>
 </Col>
 <Col lg={2} className="text-lg-end">
 <div className="text-muted small">
 Page {safePage} of {totalPages}
 </div>
 </Col>
 </Row>
 </Card.Header>

 <Card.Body className="p-0">
 {loading ? (
 <div className="py-5 text-center">
 <Spinner animation="border" variant="primary" />
 <div className="mt-3 text-muted">Loading pending approvals...</div>
 </div>
 ) : filteredRecords.length === 0 ? (
 <div className="py-5 text-center">
 <div className="mb-2 fw-semibold">No pending approvals found</div>
 <div className="text-muted">
 Everything currently visible is alapprovey cleared or filtered out.
 </div>
 </div>
 ) : (
 <div className="table-responsive">
 <Table hover className="mb-0 align-middle">
 <thead style={{ background: "#F8FAFC" }}>
 <tr>
 <th>Module</th>
 <th>Reference</th>
 <th>Project</th>
 <th>Work Order</th>
 <th>Owner</th>
 <th>Status</th>
 <th>Date</th>
 <th>Remarks</th>
 <th className="text-end">Action</th>
 </tr>
 </thead>
 <tbody>
 {paginatedRecords.map((record) => (
 <tr key={`${record.module}-${record.id}`}>
 <td>
 <Badge bg="dark" className="rounded-pill">
 {record.module}
 </Badge>
 </td>
 <td className="fw-semibold">{record.reference}</td>
 <td>{record.project}</td>
 <td>{record.workOrder}</td>
 <td>{record.owner}</td>
 <td>
 <Badge bg={getStatusVariant(record.status)}>{record.status}</Badge>
 </td>
 <td>{formatDate(record.date)}</td>
 <td style={{ maxWidth: 260 }}>
 <div className="text-truncate" title={record.remarks || ""}>
 {record.remarks || "-"}
 </div>
 </td>
 <td className="text-end">
 <Dropdown align="end">
 <Dropdown.Toggle
 variant="light"
 size="sm"
 className="border-0 rounded-circle d-inline-flex align-items-center justify-content-center"
 style={{ width: 34, height: 34 }}
 >
 <i className="fas fa-ellipsis-v" />
 </Dropdown.Toggle>
 <Dropdown.Menu as={PortalMenu} style={{ zIndex: 99999 }}>
 <Dropdown.Item onClick={() => openViewModal(record)}>
 <i className="fas fa-eye me-2" />
 View
 </Dropdown.Item>
 {canUseApprovalAction(record, "approve") && (
 <Dropdown.Item onClick={() => handleApprove(record)}>
 <i className="fas fa-check me-2 text-success" />
 Approve
 </Dropdown.Item>
 )}
 {canUseApprovalAction(record, "reject") && (
 <Dropdown.Item onClick={() => handleReject(record)}>
 <i className="fas fa-times me-2 text-danger" />
 Reject
 </Dropdown.Item>
 )}
 </Dropdown.Menu>
 </Dropdown>
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 <AppPagination
 currentPage={safePage}
 totalPages={totalPages}
 totalCount={totalFiltered}
 itemsLength={paginatedRecords.length}
 limit={pageSize}
 onPageChange={setCurrentPage}
 />
 </Card>

 <Modal show={showViewModal} onHide={() => setShowViewModal(false)} centered size="lg">
 <Modal.Header closeButton>
 <Modal.Title>Approval Details</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 {selectedRecord ? (
 <div className="d-grid gap-3">
 <div className="d-flex flex-wrap gap-2 align-items-center">
 <Badge bg="dark" className="rounded-pill">{selectedRecord.module}</Badge>
 <Badge bg={getStatusVariant(selectedRecord.status)} className="rounded-pill">
 {selectedRecord.status}
 </Badge>
 </div>

 <div className="row g-3">
 <div className="col-md-6">
 <div className="text-muted small">Reference</div>
 <div className="fw-semibold">{selectedRecord.reference}</div>
 </div>
 <div className="col-md-6">
 <div className="text-muted small">Owner</div>
 <div className="fw-semibold">{selectedRecord.owner || "-"}</div>
 </div>
 <div className="col-md-6">
 <div className="text-muted small">Project</div>
 <div className="fw-semibold">{selectedRecord.project || "-"}</div>
 </div>
 <div className="col-md-6">
 <div className="text-muted small">Work Order</div>
 <div className="fw-semibold">{selectedRecord.workOrder || "-"}</div>
 </div>
 <div className="col-md-6">
 <div className="text-muted small">Date</div>
 <div className="fw-semibold">{formatDate(selectedRecord.date)}</div>
 </div>
 <div className="col-12">
 <div className="text-muted small">Remarks</div>
 <div className="fw-semibold">{selectedRecord.remarks || "-"}</div>
 </div>
 </div>

 <div>
 <div className="text-muted small mb-2">Attachments</div>
 {selectedRecordAttachments.length ? (
 <div className="d-grid gap-2">
 {selectedRecordAttachments.map((file, index) => (
 <a
 key={`${file.fileUrl}-${index}`}
 href={resolveAttachmentUrl(file.fileUrl)}
 target="_blank"
 rel="noreferrer"
 className="btn btn-outline-primary btn-sm text-start"
 >
 <i className="fas fa-paperclip me-2" />
 {file.fileName || `Attachment ${index + 1}`}
 </a>
 ))}
 </div>
 ) : (
 <div className="text-muted">No attachment available on this record.</div>
 )}
 </div>
 </div>
 ) : null}
 </Modal.Body>
 <Modal.Footer>
 {selectedRecord?.actionRoute && (
 <Button as={Link} to={selectedRecord.actionRoute} variant="outline-primary">
 Open Record
 </Button>
 )}
 <Button variant="secondary" onClick={() => setShowViewModal(false)}>
 Close
 </Button>
 </Modal.Footer>
 </Modal>

 <SignatureModal
 show={showSignModal}
 onHide={() => {
 setShowSignModal(false);
 setPendingAction(null);
 }}
 onSave={handleSignatureSave}
 title={
 pendingAction?.record?.module
 ? `Approve ${pendingAction.record.module}`
 : "Capture Signature"
 }
 showStampUpload={showStampUploadForApproval(pendingAction?.record || {})}
 />
 </>
 );
};

export default PendingApprovals;
